import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import LoadingSpinner from './LoadingSpinner';

function formatTimeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'JUST NOW';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}M AGO`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}H AGO`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}D AGO`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
}

export default function NotificationDropdown({ onClose }) {
  const navigate = useNavigate();
  const { notifications, unreadCount, isLoading, fetchNotifications, markAsRead, markAllAsRead } = useNotifications();

  // Fetch notifications when dropdown opens
  useEffect(() => {
    fetchNotifications(1, 10);
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    onClose();
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  const handleViewAll = () => {
    onClose();
    navigate('/notifications');
  };

  return (
    <div className="absolute top-full right-0 mt-2 w-80 sm:w-96 border-2 border-black bg-white z-50 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black">
        <span className="font-mono text-sm font-bold uppercase tracking-wider">
          NOTIFICATIONS
        </span>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="font-mono text-xs uppercase tracking-wider text-gray-600 hover:text-black transition-colors"
          >
            MARK ALL READ
          </button>
        )}
      </div>

      {/* Notification List */}
      <div className="max-h-[28rem] overflow-y-auto">
        {isLoading && notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <LoadingSpinner label="LOADING..." />
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <span className="font-mono text-sm text-gray-500">NO NOTIFICATIONS YET</span>
          </div>
        ) : (
          notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full text-left px-4 py-3 border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                !notification.isRead ? 'bg-gray-50' : ''
              }`}
            >
              <div className="flex gap-3">
                {/* Unread indicator */}
                <div className="flex-shrink-0 pt-1">
                  {!notification.isRead ? (
                    <div className="w-2 h-2 bg-black" />
                  ) : (
                    <div className="w-2 h-2" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 border border-gray-300">
                      {notification.typeDisplay}
                    </span>
                    <span className="font-mono text-[10px] text-gray-400 ml-auto flex-shrink-0">
                      {formatTimeAgo(notification.createdAt)}
                    </span>
                  </div>
                  <p className="font-mono text-xs font-semibold text-black truncate">
                    {notification.title}
                  </p>
                  {notification.message && (
                    <p className="font-mono text-[11px] text-gray-500 truncate mt-0.5">
                      {notification.message}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t-2 border-black">
          <button
            onClick={handleViewAll}
            className="w-full px-4 py-3 font-mono text-xs font-bold uppercase tracking-wider text-center hover:bg-gray-50 transition-colors"
          >
            VIEW ALL NOTIFICATIONS
          </button>
        </div>
      )}
    </div>
  );
}
